import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getUbiquitiInternetLivePoint, getUbiquitiInternetSummary } from "../../scripts/api_ubiquiti";

const INTERNET_SUMMARY_QUERY_OPTIONS = {
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 30000,
};

const INTERNET_LATENCY_PROVIDERS = [
  { label: "Microsoft", logo: "/assets/microsoft.png", tone: "is-microsoft" },
  { label: "Google", logo: "/assets/google_logo.png", tone: "is-google" },
  { label: "Cloudflare", logo: "/assets/cloudflare_logo.png", tone: "is-cloudflare" },
];

const ISP_SPARKLINE_WIDTH = 100;
const ISP_SPARKLINE_HEIGHT = 40;
const MAX_GRAPH_POINTS = 35;
const LIVE_POINT_DELAY_MS = 1000;

const getWrappedPayload = (response) => {
  const firstPayload = response?.data || response || {};

  return firstPayload?.data || firstPayload;
};

const getDisplayValue = (value, fallback = "-") => (
  value === undefined || value === null || value === "" ? fallback : value
);

const getHealthDisplay = (value) => (
  value !== undefined && value !== null && value !== ""
    ? String(value).endsWith("%")
      ? value
      : `${value}%`
    : "-"
);

const hasDisplayValue = (value) => (
  value !== undefined && value !== null && value !== "" && value !== "-"
);

const toDisplayNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value !== "string") {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : null;
  }

  const matchedValue = value.trim().match(/-?\d+(?:[\s.,]\d+)*/)?.[0];

  if (!matchedValue) {
    return null;
  }

  const compactValue = matchedValue.replace(/\s/g, "");
  const lastCommaIndex = compactValue.lastIndexOf(",");
  const lastDotIndex = compactValue.lastIndexOf(".");
  const decimalSeparator = lastCommaIndex > lastDotIndex ? "," : lastDotIndex > -1 ? "." : "";
  const normalizedValue = !decimalSeparator
    ? compactValue
    : compactValue
      .replace(decimalSeparator === "," ? /\./g : /,/g, "")
      .replace(decimalSeparator, ".");
  const numericValue = Number(normalizedValue);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const getLatencyCards = (latencyChecks = []) => {
  const checks = Array.isArray(latencyChecks) ? latencyChecks : [];

  return INTERNET_LATENCY_PROVIDERS.map((provider) => {
    const check = checks.find((item) => item?.label === provider.label);

    return {
      label: provider.label,
      logo: provider.logo,
      tone: provider.tone,
      display: getDisplayValue(check?.display),
    };
  });
};

const getTrafficPointValue = (point, key) => {
  const displayKey = key === "downloadMbps" ? "downloadDisplay" : "uploadDisplay";
  const value = point?.[key] ?? point?.[displayKey];
  const numericValue = toDisplayNumber(value);

  return numericValue !== null ? Math.max(0, numericValue) : null;
};

const getTrafficPointCoordinates = (points, key, maxValue) => points
  .map((point, index) => {
    const value = getTrafficPointValue(point, key);
    const x = points.length > 1
      ? (index / (points.length - 1)) * ISP_SPARKLINE_WIDTH
      : ISP_SPARKLINE_WIDTH / 2;
    const y = maxValue > 0
      ? ISP_SPARKLINE_HEIGHT - 2 - ((value / maxValue) * (ISP_SPARKLINE_HEIGHT - 6))
      : ISP_SPARKLINE_HEIGHT - 2;

    return { x, y, value };
  })
  .filter((point) => point.value !== null);

const buildTrafficStepPath = (points, key, maxValue) => {
  const validPoints = points
    .map((point, index) => ({ value: getTrafficPointValue(point, key), index }))
    .filter((point) => point.value !== null);

  if (validPoints.length === 1) {
    const y = maxValue > 0
      ? ISP_SPARKLINE_HEIGHT - 2 - ((validPoints[0].value / maxValue) * (ISP_SPARKLINE_HEIGHT - 6))
      : ISP_SPARKLINE_HEIGHT - 2;

    return `M 0 ${y.toFixed(2)} L ${ISP_SPARKLINE_WIDTH} ${y.toFixed(2)}`;
  }

  const coordinates = getTrafficPointCoordinates(points, key, maxValue);

  return coordinates
    .reduce((pathParts, point, index) => {
      if (index === 0) {
        return [`M ${point.x.toFixed(2)} ${point.y.toFixed(2)}`];
      }

      const previousPoint = coordinates[index - 1];

      return [
        ...pathParts,
        `L ${point.x.toFixed(2)} ${previousPoint.y.toFixed(2)}`,
        `L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
      ];
    }, [])
    .join(" ");
};

const buildTrafficAreaPath = (stepPath) => {
  const coordinates = stepPath.match(/-?\d+(?:\.\d+)? -?\d+(?:\.\d+)?/g) || [];

  if (!coordinates.length) {
    return "";
  }

  return `${stepPath} L ${ISP_SPARKLINE_WIDTH} ${ISP_SPARKLINE_HEIGHT - 2} L 0 ${ISP_SPARKLINE_HEIGHT - 2} Z`;
};

const getTrafficSparklineData = (points = []) => {
  const graphPoints = Array.isArray(points) ? points : [];
  const downloadValues = graphPoints
    .map((point) => getTrafficPointValue(point, "downloadMbps"))
    .filter(Number.isFinite);
  const uploadValues = graphPoints
    .map((point) => getTrafficPointValue(point, "uploadMbps"))
    .filter(Number.isFinite);
  const values = [...downloadValues, ...uploadValues];

  if (!graphPoints.length) {
    return {
      pointCount: graphPoints.length,
      downloadValueCount: downloadValues.length,
      uploadValueCount: uploadValues.length,
      downloadAreaPath: "",
      downloadPath: "",
      uploadPath: "",
    };
  }

  if (!values.length) {
    return {
      pointCount: graphPoints.length,
      downloadValueCount: downloadValues.length,
      uploadValueCount: uploadValues.length,
      downloadAreaPath: "",
      downloadPath: "",
      uploadPath: "",
    };
  }

  const maxValue = Math.max(...values);
  const downloadPath = buildTrafficStepPath(graphPoints, "downloadMbps", maxValue);

  return {
    pointCount: graphPoints.length,
    downloadValueCount: downloadValues.length,
    uploadValueCount: uploadValues.length,
    downloadAreaPath: buildTrafficAreaPath(downloadPath),
    downloadPath,
    uploadPath: buildTrafficStepPath(graphPoints, "uploadMbps", maxValue),
  };
};

const isInternetLivePoint = (point) => (
  point
  && typeof point === "object"
  && !Array.isArray(point)
  && (
    getTrafficPointValue(point, "downloadMbps") !== null
    || getTrafficPointValue(point, "uploadMbps") !== null
  )
);

const LoadingValue = ({ children, className = "", isLoading }) => {
  const content = getDisplayValue(children);
  const hasValue = hasDisplayValue(content);

  if (isLoading && !hasValue) {
    return <span className={`ubiquiti-loading-value ${className}`.trim()} aria-label="Loading"></span>;
  }

  return (
    <span className={className}>
      {content}
    </span>
  );
};

const UbiquitiIspSparkline = ({ sparkline }) => (
  <svg className="ubiquiti-isp-sparkline" viewBox={`0 0 ${ISP_SPARKLINE_WIDTH} ${ISP_SPARKLINE_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
    <line className="is-empty-line" x1="0" y1={ISP_SPARKLINE_HEIGHT - 2} x2={ISP_SPARKLINE_WIDTH} y2={ISP_SPARKLINE_HEIGHT - 2} />
    {sparkline.downloadAreaPath ? (
      <path className="is-download-area" d={sparkline.downloadAreaPath} />
    ) : null}
    {sparkline.downloadPath ? (
      <path className="is-download" d={sparkline.downloadPath} />
    ) : null}
    {sparkline.uploadPath ? (
      <path className="is-upload" d={sparkline.uploadPath} />
    ) : null}
  </svg>
);

const UbiquitiIspPanel = ({
  apiUrl,
  enabled = true,
  livePointEnabled = enabled,
  onQueryStateChange,
  refreshKey = 0,
  siteId,
}) => {
  const { t } = useTranslation();
  const [graphPoints, setGraphPoints] = useState([]);
  const [isLivePointLoading, setIsLivePointLoading] = useState(false);
  const summaryQuery = useQuery({
    ...INTERNET_SUMMARY_QUERY_OPTIONS,
    queryKey: ["ubiquiti-internet-summary", apiUrl, siteId],
    queryFn: () => getUbiquitiInternetSummary(apiUrl, siteId),
    enabled: enabled && !!apiUrl && !!siteId,
  });
  const { refetch: refetchSummary } = summaryQuery;
  const summary = useMemo(() => getWrappedPayload(summaryQuery.data), [summaryQuery.data]);
  const latestLivePoint = graphPoints.at(-1) || null;
  const sparkline = useMemo(() => getTrafficSparklineData(graphPoints), [graphPoints]);
  const latencyCards = useMemo(() => getLatencyCards(summary.latencyChecks), [summary.latencyChecks]);
  const ispName = getDisplayValue(summary.isp?.name);
  const ipv4 = getDisplayValue(summary.ipv4?.value);
  const monthlyUsage = getDisplayValue(summary.monthlyDataUsage?.display);
  const download = getDisplayValue(latestLivePoint?.downloadDisplay);
  const upload = getDisplayValue(latestLivePoint?.uploadDisplay);
  const hasHealth = summary.isp?.healthPercent !== undefined
    && summary.isp?.healthPercent !== null
    && summary.isp?.healthPercent !== "";
  const health = getHealthDisplay(summary.isp?.healthPercent);
  const isOrangeIsp = String(ispName).toLowerCase().includes("orange");
  const isSummaryInitialLoading = !summaryQuery.data && (!enabled || summaryQuery.isLoading);
  const canLoadLivePoint = enabled
    && livePointEnabled
    && (summaryQuery.isFetched || summaryQuery.isError);
  const isLivePointInitialLoading = !latestLivePoint && (!canLoadLivePoint || isLivePointLoading);

  useEffect(() => {
    if (enabled && refreshKey && siteId) {
      refetchSummary();
    }
  }, [enabled, refreshKey, refetchSummary, siteId]);

  useEffect(() => {
    if (!onQueryStateChange) {
      return;
    }

    onQueryStateChange({
      siteId,
      isError: summaryQuery.isError,
      isFetched: summaryQuery.isFetched,
      isFetching: summaryQuery.isFetching,
    });
  }, [
    onQueryStateChange,
    siteId,
    summaryQuery.isError,
    summaryQuery.isFetched,
    summaryQuery.isFetching,
  ]);

  useEffect(() => {
    setGraphPoints([]);
    setIsLivePointLoading(false);
  }, [apiUrl, siteId]);

  useEffect(() => {
    if (!apiUrl || !siteId) {
      setGraphPoints([]);
      setIsLivePointLoading(false);
      return undefined;
    }

    if (!canLoadLivePoint) {
      setIsLivePointLoading(false);
      return undefined;
    }

    let stopped = false;
    let timeoutId = null;

    setGraphPoints([]);

    const loadPoint = async () => {
      if (stopped) {
        return;
      }

      if (document.hidden) {
        if (!stopped) {
          timeoutId = window.setTimeout(loadPoint, LIVE_POINT_DELAY_MS);
        }
        return;
      }

      try {
        setIsLivePointLoading(true);
        const point = getWrappedPayload(await getUbiquitiInternetLivePoint(apiUrl, siteId));

        if (stopped) {
          return;
        }

        if (isInternetLivePoint(point)) {
          setGraphPoints((previousPoints) => [...previousPoints, point].slice(-MAX_GRAPH_POINTS));
        }
      } catch (error) {
        window.__ubiquitiInternetLivePointError = error;
      } finally {
        if (!stopped) {
          setIsLivePointLoading(false);
        }
        if (!stopped) {
          timeoutId = window.setTimeout(loadPoint, LIVE_POINT_DELAY_MS);
        }
      }
    };

    loadPoint();

    return () => {
      stopped = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [apiUrl, canLoadLivePoint, siteId]);

  useEffect(() => {
    window.__ubiquitiInternetSummaryDebug = {
      apiCall: `/ubiquiti/sites/${siteId}/internet-summary`,
      enabled,
      queryStatus: {
        isLoading: summaryQuery.isLoading,
        isFetching: summaryQuery.isFetching,
        isError: summaryQuery.isError,
        error: summaryQuery.error,
      },
      rawResponse: summaryQuery.data,
      diagnostics: summary.diagnostics || null,
      evidence: {
        throughput: summary.throughput?.evidence || null,
        latencyChecks: summary.latencyChecks || [],
      },
      displayedValues: {
        ispName,
        health,
        hasHealth,
        ipv4,
        monthlyDataUsage: monthlyUsage,
        latencyChecks: latencyCards,
      },
    };
  }, [
    enabled,
    health,
    hasHealth,
    ispName,
    ipv4,
    latencyCards,
    monthlyUsage,
    siteId,
    summary,
    summaryQuery.data,
    summaryQuery.error,
    summaryQuery.isError,
    summaryQuery.isFetching,
    summaryQuery.isLoading,
  ]);

  useEffect(() => {
    window.__ubiquitiInternetLivePointDebug = {
      apiCall: `/ubiquiti/sites/${siteId}/internet-live-point`,
      canLoadLivePoint,
      enabled,
      livePointEnabled,
      points: graphPoints,
      latestPoint: latestLivePoint,
      isLoading: isLivePointLoading,
      displayedValues: {
        download,
        upload,
      },
      sparkline,
    };
  }, [canLoadLivePoint, download, enabled, graphPoints, isLivePointLoading, latestLivePoint, livePointEnabled, siteId, sparkline, upload]);

  return (
    <article className="ubiquiti-isp-card">
      <div className="ubiquiti-isp-card-header">
        {isOrangeIsp ? (
          <img className="ubiquiti-isp-logo" src="/assets/orange_logo.svg" alt="" aria-hidden="true" />
        ) : (
          <span className="ubiquiti-isp-color"></span>
        )}
          <LoadingValue isLoading={isSummaryInitialLoading}>{ispName}</LoadingValue>
        <small className={hasHealth ? "has-health" : ""}>
          <LoadingValue isLoading={isSummaryInitialLoading}>{health}</LoadingValue>
        </small>
      </div>
      <div className="ubiquiti-info-list">
        <div>
          <span>{t("IPv4 Address")}</span>
            <LoadingValue isLoading={isSummaryInitialLoading}>{ipv4}</LoadingValue>
        </div>
        <div>
          <span>{t("Monthly data usage")}</span>
            <LoadingValue isLoading={isSummaryInitialLoading}>{monthlyUsage}</LoadingValue>
        </div>
        <div>
          <span>{t("Throughput")}</span>
          <div className="ubiquiti-throughput-values">
            <span className="is-download" aria-label="Download">
              <span aria-hidden="true">&darr;</span>
              <LoadingValue isLoading={isLivePointInitialLoading}>{download}</LoadingValue>
            </span>
            <span className="is-upload" aria-label="Upload">
              <span aria-hidden="true">&uarr;</span>
              <LoadingValue isLoading={isLivePointInitialLoading}>{upload}</LoadingValue>
            </span>
          </div>
        </div>
      </div>
      <div className={isLivePointInitialLoading ? "ubiquiti-isp-sparkline-wrap is-loading" : "ubiquiti-isp-sparkline-wrap"}>
        <UbiquitiIspSparkline sparkline={sparkline} />
      </div>
      <div className="ubiquiti-latency-cards">
        {latencyCards.map((card) => (
          <span className={card.tone} key={card.label}>
            <div>
              <img src={card.logo} alt="" aria-hidden="true" />
            </div>
            <LoadingValue isLoading={isSummaryInitialLoading}>{card.display}</LoadingValue>
          </span>
        ))}
      </div>
    </article>
  );
};

export default UbiquitiIspPanel;
